<template>
  <div class="settings">
    <sticky-header :title="$t('settings.main.title')" />

    <block :title="$t('settings.main.basicFunctions')">
      <settings-basic-block
        slot="content"
        :title="$t('settings.main.reservations')"
        icon="calendar-big-color"
        color="#E9F5FE"
        url="/settings/reservations" />

      <settings-basic-block
        slot="content"
        :title="$t('settings.main.emails')"
        icon="mail-big-color"
        color="#F4F1FC"
        url="/settings/notifications/emails" />

      <settings-basic-block
        slot="content"
        :title="$t('settings.main.plugin')"
        icon="layers-big-color"
        color="#FEF7EB"
        url="/settings/plugin" />
      <settings-basic-block
        slot="content"
        :title="$t('settings.main.gdpr')"
        icon="rodo-big-color"
        color="#fbe4e5"
        url="/settings/gdpr" />
    </block>

    <block :title="$t('settings.main.additionalFunctions')">
      <ul
        class="settings-list"
        slot="content">
        <li
          v-for="(item, index) in settingsFunctionsList"
          :key="'setting-function-' + index"
          :class="{ 'settings-item': true, 'is-premium': !userHasAccessTo(item.setting) }">
          <switcher
            :ref="'setting-' + item.setting"
            :key="'setting-function-switcher-' + item.setting"
            :value="getSetting(item.setting)"
            @click.native="setSetting(item.setting)"
            :disabled="((item.setting === 'workers' || item.setting === 'daily_reservations') && !getSetting('services'))" />
          <span class="settings-item-name">{{ $t(item.name) }}</span>
          <span
            class="settings-item-desc"
            v-pure-html="getItemDesc(item)">
          </span>

          <btn
            v-if="item.url && status[item.setting] && userHasAccessTo(item.setting) && !(item.setting === 'workers' && !getSetting('services'))"
            :url="item.url"
            type="with-icon small"
            :key="'setting-function-button-' + index"
            iconName="cog"
            iconColor="color-text-light">
            {{ $t('settings.main.adjustFunction') }}
          </btn>

          <span
            v-if="userHasAccessTo(item.setting) && ((item.setting === 'workers' && !getSetting('services')) || (item.setting === 'daily_reservations' && !getSetting('services')))"
            :key="'setting-integration-info-' + index"
            class="setting-dependency-info">
            {{ $t('settings.main.thisFunctionHasRequirements') }}
          </span>

          <btn
            v-if="!userHasAccessTo(item.setting)"
            url="/billing/"
            :key="'setting-integration-upsell-button-' + index"
            type="small success">
            {{ $t('settings.main.buyHigherPackage') }}
          </btn>
        </li>
      </ul>
    </block>

    <block :title="$t('settings.main.availableIntegrations')">
      <ul
        class="settings-list"
        slot="content">
        <li
          v-for="(item, index) in settingsIntegrationsList"
          :key="'setting-integration-' + index"
          :class="{ 'settings-item': true, 'is-premium': !userHasAccessTo(item.setting) }">
          <switcher
            :ref="'setting-' + item.setting"
            :key="'setting-integration-switcher-' + item.setting"
            :value="getSetting(item.setting)"
            @click.native="setSetting(item.setting)"
            :disabled="((item.setting === 'workers' || item.setting === 'daily_reservations') && !getSetting('services')) || (item.setting === 'fakturownia_api' && !getSetting('payment'))" />
          <span class="settings-item-name">{{ $t(item.name) }}</span>
          <span
            class="settings-item-desc"
            v-pure-html="getItemDesc(item)">
          </span>

          <btn
            v-if="item.url && status[item.setting] && userHasAccessTo(item.setting) && !(item.setting === 'workers' && !getSetting('services')) && !(item.setting === 'fakturownia_api' && !getSetting('payment'))"
            :url="item.url"
            :key="'setting-integration-button-' + index"
            type="with-icon small"
            iconName="cog"
            iconColor="color-text-light">
            {{ $t('settings.main.adjustFunction') }}
          </btn>

          <span
            v-if="userHasAccessTo(item.setting) && ((item.setting === 'workers' && !getSetting('services')) || (item.setting === 'daily_reservations' && !getSetting('services')))"
            :key="'setting-integration-info-' + index"
            class="setting-dependency-info">
            {{ $t('settings.main.thisFunctionHasRequirements') }}
          </span>

          <span
            v-if="userHasAccessTo(item.setting) && (item.setting === 'fakturownia_api' && !getSetting('payment'))"
            :key="'setting-integration-info-' + index"
            class="setting-dependency-info">
            {{ $t('settings.main.thisFunctionHasRequirementPayment') }}
          </span>

          <btn
            v-if="!userHasAccessTo(item.setting)"
            url="/billing/"
            :key="'setting-integration-upsell-button-' + index"
            type="small success">
            {{ $t('settings.main.buyHigherPackage') }}
          </btn>
        </li>
      </ul>
    </block>
  </div>
</template>

<script>
import Vue from 'vue';
import Block from './../common/Block';
import ErrorUtils from './../helpers/ErrorUtils.js';
import SettingsBasicBlock from './../ui/SettingsBasicBlock';
import StickyHeader from './../common/StickyHeader';
import settingsList from './../helpers/settings-list';

export default {
  name: 'settings',
  components: {
    'block': Block,
    'settings-basic-block': SettingsBasicBlock,
    'sticky-header': StickyHeader
  },
  data () {
    return {
      settingsList: [],
      status: {},
      settingsFunctionsList: [],
      settingsIntegrationsList: []
    };
  },
  mounted () {
    if (this.$store.state.config.user.id !== 0) {
      this.setStatuses(settingsList);
      this.settingsList = settingsList.slice();
      this.settingsFunctionsList = this.settingsList.filter(setting => !setting.isIntegration);
      this.settingsIntegrationsList = this.settingsList.filter(setting => setting.isIntegration);
    }

    this.$bus.$on('configuration-loaded', this.configurationLoad);
  },
  methods: {
    configurationLoad () {
      this.setStatuses(settingsList);
      this.settingsList = settingsList.slice();
      this.settingsFunctionsList = this.settingsList.filter(setting => !setting.isIntegration);
      this.settingsIntegrationsList = this.settingsList.filter(setting => setting.isIntegration);
    },
    getSetting (settingName) {
      return !!this.$store.state.config.company[settingName];
    },
    setStatuses (settingsList) {
      settingsList.forEach(item => {
        this.status[item.setting] = this.getSetting(item.setting);
      });
    },
    setSetting (settingName, valueToSet = null, storeUpdateOnly = false) {
      if (valueToSet !== null && valueToSet === this.getSetting(settingName)) {
        return;
      }

      let newValue = !this.getSetting(settingName);

      if (valueToSet !== null) {
        newValue = valueToSet;
      }

      this.$store.commit('config/setCompanyConfig', {
        field: settingName,
        value: newValue ? 1 : 0
      });

      if (settingName === 'services' && !newValue && this.$store.state.config.company.time_increaser === -1) {
        this.$store.commit('config/setTimeIncreaser', 15);
      }

      if (settingName === 'services' && !newValue && this.userHasAccessTo('workers')) {
        this.setSetting('workers', false, true);
        this.setSetting('discounts', false, true);
      }

      if (settingName === 'services' && !newValue) {
        this.setSetting('discounts', false, true);
      }

      if (settingName === 'services' && !newValue && this.userHasAccessTo('daily_reservations')) {
        this.setSetting('daily_reservations', false, true);
      }

      if (settingName === 'payment' && !newValue && this.userHasAccessTo('fakturownia_api')) {
        this.setSetting('fakturownia_api', false, true);
      }

      if (settingName === 'payment' && !newValue) {
        this.setSetting('discounts', false, true);
        this.setSetting('passes', false, true);
        this.setSetting('commissions', false, true);
      }

      if (settingName === 'workers' && !newValue) {
        this.setSetting('commissions', false, true);
      }

      if (settingName === 'outlook_calendar' && newValue && this.userHasAccessTo('google_calendar')) {
        if (this.getSetting('google_calendar')) {
          this.setSetting('google_calendar', false, true);
        }
      }

      if (settingName === 'google_calendar' && newValue && this.userHasAccessTo('outlook_calendar')) {
        if (this.getSetting('outlook_calendar')) {
          this.setSetting('outlook_calendar', false, true);
        }
      }

      Vue.set(this.status, settingName, newValue);

      if (!storeUpdateOnly) {
        this.$http.post('/panel/settings/settings-change-functionality', {
          type: settingName
        }).then(response => {
          this.$bus.$emit('notification-display', {
            type: 'success',
            message: this.$t('settings.main.settingsSaved')
          });
        }).catch(error => {
          ErrorUtils.handleSubmitError(error, '/panel/settings/settings-change-functionality', this.$router, this.$bus, this.setSetting.bind(this, settingName, valueToSet, storeUpdateOnly));
        });
      }
    },
    userHasAccessTo (permissionName) {
      let limitedPermissions = [
        'my_page',
        'reservation_parameters',
        'payment',
        'sms',
        'workers',
        'google_calendar',
        'outlook_calendar',
        'multilang',
        'fakturownia_api',
        'getresponse',
        'daily_reservations'
      ];

      if (limitedPermissions.indexOf(permissionName) > -1) {
        return !!this.$store.state.config.permissions[permissionName];
      }

      return true;
    },
    getItemDesc (item) {
      return this.$t(item.desc) + (item.isBeta ? ' <span class="beta-badge">BETA</span>' : '');
    }
  },
  beforeDestroy () {
    this.$bus.$off('configuration-loaded', this.configurationLoad);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.settings {
  padding: 8rem 0 2rem 0;
  width: 100%;

  .block {
    .basic-block {
      width: 24%;
    }
  }

  /deep/ .beta-badge {
    background: $color-danger;
    border-radius: $border-radius-small;
    color: $color-light;
    display: inline;
    font-size: 1.2rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1;
    margin-left: .5rem;
    padding: 2px 4px;
    position: relative;
    top: -2px;
  }

  .settings {
    list-style-type: none;
    padding: 2rem 2.5rem;

    &-list {
      list-style-type: none;
      margin: -3.5rem -2.5rem;
      padding: 0;
      width: calc(100% + 5rem);
    }

    &-item {
      align-items: center;
      border-top: 1px solid $color-border-light;
      display: flex;
      padding: 2rem 2.5rem;

      &:first-child {
        border-top: none;
      }

      &.is-premium {
        .switcher,
        .settings-item-name,
        .settings-item-desc {
          opacity: .5;
          pointer-events: none;
        }

        .switcher {
          opacity: .4;
        }
      }

      &-name {
        font-size: 1.4rem;
        font-weight: 600;
        margin-left: 2rem;
        padding: .5rem 0;
        width: 26rem;
      }

      &-desc {
        color: $color-text-light;
        font-size: 1.4rem;
        font-style: italic;
      }

      .button {
        margin-left: auto;
      }
    }
  }

  .setting-dependency-info {
    color: $color-danger;
    font-size: 1.4rem;
    font-style: italic;
    margin-left: auto;
  }
}

@media (max-width: 1600px) {
  .settings {
    .settings-item-desc {
      max-width: calc(100% - 500px);
    }
  }
}

@media (max-width: 1240px) {
  .settings {
    .settings {
      &-list {
        margin: 0 -1rem;
      }
    }
  }
}

@media (max-width: 1100px) {
  .settings {
    .block {
      .basic-block {
        width: 49%;

        &:nth-child(1),
        &:nth-child(2) {
          margin-bottom: 2%;
        }
      }
    }

    .settings-item {
      flex-wrap: wrap;

      .settings-item-name {
        width: calc(100% - 6rem);
      }

      .settings-item-desc {
        max-width: 50%;
      }
    }
  }
}

@media (max-width: 640px) {
  .settings {
    margin-top: 5rem!important;
  }
}

@media (max-width: 480px) {
  .settings {
    .block {
      .basic-block {
        width: 100%;

        &:nth-child(1),
        &:nth-child(2),
        &:nth-child(3) {
          margin-bottom: 2%;
        }
      }
    }

    .settings-item {
      .settings-item-desc {
        margin-top: 1rem;
        max-width: 100%;
      }

      .button {
        justify-content: center;
        margin-bottom: 0;
        margin-top: 2rem;
        width: 100%;
      }
    }
  }
}

@supports (position: sticky) {
  .settings {
    padding-top: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/Settings.vue